import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import { ReadOnlyField } from "@/pages/reports/rollshop-operations/components/ReadOnlyField";
import type { RollBlank } from "../models/RollBlank";
import { MillSizeSelector } from "./MillSizeSelector";
import { RollApplicationSelector } from "./RollApplicationSelector";
import { RollMaterialSelector } from "./RollMaterialSelector";
import { RollBlankAttachments } from "./RollBlankAttachments";

interface FormErrors {
	Dwg?: string;
}

interface RollBlankDetailViewerProps {
	blank: RollBlank | null;
	existingBlanks: RollBlank[];
	isSaving: boolean;
	onSave: (blank: RollBlank) => void;
	onClose: () => void;
}

export const RollBlankDetailViewer = ({
	blank,
	existingBlanks,
	isSaving,
	onSave,
	onClose,
}: RollBlankDetailViewerProps) => {
	const [formData, setFormData] = useState<RollBlank | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewBlank = !blank?.Id;

	const validateForm = useMemo(() => {
		return (data: RollBlank | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Dwg || data.Dwg.trim().length === 0) {
				newErrors.Dwg = "Dwg is required";
			} else {
				const isDuplicate = existingBlanks.some(
					(b) =>
						b.Id !== data.Id &&
						b.Dwg.toLowerCase() === data.Dwg.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Dwg = "This Dwg already exists";
				}
			}

			return newErrors;
		};
	}, [existingBlanks]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewBlank && isFormValid;

	useEffect(() => {
		if (blank) {
			setFormData(sanitizeNullStrings({ ...blank }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [blank]);

	const handleChange = (field: keyof RollBlank, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof RollBlank) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof RollBlank, value: unknown) => {
		handleBlur(field);

		if (isNewBlank) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = blank?.[field];
		if (originalValue !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleSelectChange = (field: keyof RollBlank, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewBlank) return;

		if (blank?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (field: keyof RollBlank, rawValue: string) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		setTouched({ Dwg: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!blank) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a roll blank to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	const numericField = (
		label: string,
		field: keyof RollBlank,
		step: number,
	) => (
		<TextField
			label={label}
			type="number"
			value={(formData[field] as number | null) ?? ""}
			onChange={(e) =>
				handleChange(
					field,
					e.target.value === "" ? null : Number.parseFloat(e.target.value),
				)
			}
			onBlur={(e) => handleNumericBlurSave(field, e.target.value)}
			fullWidth
			size="small"
			sx={EDITABLE_FIELD_SX}
			slotProps={{
				inputLabel: { shrink: true },
				htmlInput: { step },
			}}
		/>
	);

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewBlank ? "New Roll Blank" : formData.Dwg}
				active={isNewBlank ? undefined : blank.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewBlank ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : undefined
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Basic Information" icon={<InfoOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Dwg"
									value={formData.Dwg}
									onChange={(e) => handleChange("Dwg", e.target.value)}
									onBlur={(e) => handleBlurSave("Dwg", e.target.value)}
									fullWidth
									size="small"
									required
									error={touched.Dwg && !!errors.Dwg}
									helperText={touched.Dwg && errors.Dwg}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<MillSizeSelector
									value={formData.MillSizeId}
									onChange={(value) => handleSelectChange("MillSizeId", value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.Active}
											onChange={(e) =>
												handleSelectChange("Active", e.target.checked)
											}
											size="small"
										/>
									}
									label="Active"
									sx={{ ml: 0 }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Dimensions" icon={<StraightenOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 4 }}>
								{numericField("Width", "Width", 0.01)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								{numericField("New Diameter", "NewDiameter", 0.01)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								{numericField("Scrap Diameter", "ScrapDiameter", 0.001)}
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Hardness" icon={<SpeedOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{numericField("Hardness Minimum", "HardnessMinimum", 0.001)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{numericField("Hardness Maximum", "HardnessMaximum", 0.001)}
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection
						title="Costs & Tolerances"
						icon={<AttachMoneyOutlinedIcon />}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{numericField("Est R Cost", "EstRCost", 0.001)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{numericField("Est S Cost", "EstSCost", 0.001)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{numericField("D Tol", "DTol", 0.001)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{numericField("D Tolm", "DTolm", 0.001)}
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="References" icon={<LinkOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<RollApplicationSelector
									value={formData.RollApplicationId}
									onChange={(value) =>
										handleSelectChange("RollApplicationId", value)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<RollMaterialSelector
									value={formData.RollMaterialId}
									onChange={(value) =>
										handleSelectChange("RollMaterialId", value)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Additional" icon={<NotesOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Description"
									value={formData.Description ?? ""}
									onChange={(e) =>
										handleChange("Description", e.target.value || null)
									}
									onBlur={(e) =>
										handleBlurSave("Description", e.target.value || null)
									}
									fullWidth
									size="small"
									multiline
									rows={3}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="PIT Program Name"
									value={formData.PitProgramName ?? ""}
									onChange={(e) =>
										handleChange("PitProgramName", e.target.value || null)
									}
									onBlur={(e) =>
										handleBlurSave("PitProgramName", e.target.value || null)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					{!isNewBlank && (
						<DetailSection
							title="Attachments"
							icon={<AttachFileOutlinedIcon />}
						>
							<RollBlankAttachments
								rollBlankId={formData.Id}
								disabled={isSaving}
							/>
						</DetailSection>
					)}

					{formData.DwgName && (
						<DetailSection
							title="DWG File (Legacy)"
							icon={<DescriptionOutlinedIcon />}
						>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField
										label="DWG File Name"
										value={formData.DwgName ?? ""}
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
					)}
				</Box>
			</DetailBody>
		</Box>
	);
};
